import { forwardRef, ReactNode, useId } from 'react';
import Image from 'next/image';
import DownArrowIcon from 'assets/svgs/DownArrowIcon';
import { H5 } from 'components/Text';
import { fieldLook } from 'utils/searchFieldLook';

// WHIT-493: the closed state of every package-search field, so the package
// and hotel tabs look like the flight tab: a 48px field, the brand's line
// icon in a 40px column (as components/Select), the value or placeholder,
// and the chevron the flight search settings use.
interface IFieldTriggerProps {
  // The field's name. Shown above the field only where the value alone
  // would not say what it is (the advanced rows); either way it is part of
  // the button's accessible name, with the value.
  label: string;
  showLabel?: boolean;
  // A static icon import from assets/svgs/brandIcons.
  icon?: string;
  open: boolean;
  filled?: boolean;
  onClick: () => void;
  // When set, a separate clear button replaces the chevron. It is a sibling
  // of the field, not inside it, so it can be reached by keyboard and a
  // click on it does not also open the field. z-40 keeps it above the
  // backdrop PackageSearchField lays over the page while a panel is open.
  onClear?: () => void;
  clearLabel?: string;
  // WHIT-497: 'setting' is the flight tab's settings row above the fields
  // (containers/SearchSettings): the value as text with the yellow chevron,
  // no box.
  variant?: 'field' | 'setting';
  children: ReactNode;
}

const FieldTrigger = forwardRef<HTMLButtonElement, IFieldTriggerProps>(
  (
    {
      label,
      showLabel,
      icon,
      open,
      filled,
      onClick,
      onClear,
      clearLabel,
      variant = 'field',
      children,
    },
    ref,
  ) => {
    const id = useId();
    if (variant === 'setting') {
      return (
        <>
          <span id={`${id}-label`} className="sr-only">
            {label}
          </span>
          <button
            ref={ref}
            type="button"
            onClick={onClick}
            aria-expanded={open}
            aria-labelledby={`${id}-label ${id}-value`}
            // As SearchSettings' triggers: 44px tall on a phone (WHIT-494).
            className="mx-auto max-lg:min-h-11 flex items-center justify-center space-x-1 text-black">
            <span id={`${id}-value`}>
              <H5 as="span" className="block whitespace-nowrap">
                {children}
              </H5>
            </span>
            <DownArrowIcon
              className={`h-4 w-4 sm:w-6 transition-transform ${
                open ? 'rotate-180' : ''
              }`}
              color="var(--tt-yellow)"
            />
          </button>
        </>
      );
    }
    return (
      <>
        <span
          id={`${id}-label`}
          className={
            showLabel
              ? 'text-xs text-tt-light-gray-purple font-medium'
              : 'sr-only'
          }>
          {label}
        </span>
        <div className="relative w-full">
          <button
            ref={ref}
            type="button"
            onClick={onClick}
            aria-expanded={open}
            aria-labelledby={`${id}-label ${id}-value`}
            className={`w-full flex items-center h-12 rounded ${
              icon ? 'pr-3' : 'px-3'
            } text-left text-base transition-all ${fieldLook({
              open,
              filled,
            })}`}>
            {icon && (
              <Image
                src={icon}
                alt=""
                style={{ color: undefined }}
                className="w-10 h-10 shrink-0 self-center pl-2 pr-2"
              />
            )}
            <span id={`${id}-value`} className="flex-1 min-w-0 truncate">
              {children}
            </span>
            {onClear ? (
              // Room for the clear button laid over this end.
              <span className="w-5 shrink-0 ml-1" />
            ) : (
              <DownArrowIcon
                className={`h-4 w-4 shrink-0 ml-1 transition-transform ${
                  open ? 'rotate-180' : ''
                }`}
                color="var(--tt-yellow)"
              />
            )}
          </button>
          {onClear && (
            <button
              type="button"
              aria-label={clearLabel}
              onClick={onClear}
              // 44px to tap (Apple HIG); the × stays where it was (WHIT-494).
              className="absolute z-40 right-0 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center text-gray-400 hover:text-gray-600 text-lg leading-none">
              ×
            </button>
          )}
        </div>
      </>
    );
  },
);

FieldTrigger.displayName = 'FieldTrigger';

export type FieldVariant = NonNullable<IFieldTriggerProps['variant']>;

// The wrapper and panel position each dropdown uses for its variant. A
// setting sizes to its text. On a phone its panel hangs from the whole
// settings row, full width, rather than from the setting: the row is
// centred and wraps, so a panel under the setting ran off the screen.
export const fieldWrapperClassName = (variant: FieldVariant = 'field') =>
  variant === 'setting'
    ? // sm:pl-2 as the flight row's triggers, so the two rows line up.
      'sm:pl-2 lg:relative'
    : 'flex flex-col gap-1 flex-1 min-w-0 relative';
export const panelPositionClassName = (variant: FieldVariant = 'field') =>
  variant === 'setting' ? 'left-0 right-0 lg:right-auto' : 'left-0';

export default FieldTrigger;
